/**
 * Country/region directory for the chatbot country allowlist (mb-server#6239).
 *
 * The stored value on `chat_bot.allowed_countries` is ALWAYS an explicit list of ISO-3166
 * alpha-2 codes. A region preset only fills that list in — nothing persists the region name, so
 * changing a preset here never silently changes what an already-saved bot does.
 *
 * ## Why this file exists rather than reusing an existing mapping
 *
 * Three incompatible region taxonomies already live in this codebase and none of them is usable
 * here:
 *
 * - `geoUtils.COUNTRY_TO_CONTINENT` / `geoipEnricher.CONTINENT_MAP` (mb-server) are 7-continent
 *   mappings. "APAC" is not a continent.
 * - `personaLocationUtils.CONTINENT_VALUE_MAP` (mb-server) maps `apac -> 'Asia'`. That is wrong
 *   in both directions for this feature: it pulls in the Middle East (which is in Asia) and it
 *   drops **Australia and New Zealand** (which are Oceania). Using it for FarEye would have
 *   hidden the widget from Australia while showing it in Dubai — the inverse of what they
 *   approved.
 * - The 7 Oct 2026 FarEye readout used a four-region split (Americas / Europe / MEA / APAC).
 *   Its published per-region counts could not be reproduced from the live visit data, and in
 *   particular its APAC figure was smaller than India's count alone, so it is not a safe source
 *   to copy either.
 *
 * So the lists below are authored, explicit, and the judgement calls are named.
 *
 * ## Judgement calls, stated rather than buried
 *
 * - **India is in APAC.** It is FarEye's home market and, measured over the 14 days to 7 Oct
 *   2026, their single largest source of identified visitor companies. Any APAC preset that
 *   omits it is broken for this customer.
 * - **Australia, New Zealand and the Pacific are in APAC.** The "-PAC" is not decorative.
 * - **The Middle East is in MEA, not APAC**, because this split keeps MEA separate from both.
 * - **Türkiye and Israel are in MEA**, following the same split. Several sales territory models
 *   put Türkiye in Europe instead; if a customer's own model differs, change their stored list
 *   rather than this preset, since the preset is only a starting point.
 * - **Russia is in Europe.**
 *
 * Country NAMES are not stored here on purpose — `Intl.DisplayNames` derives them from the code
 * at render time, so there is no parallel list of 249 names to drift.
 */

/** Asia-Pacific, excluding the Middle East. */
export const APAC_COUNTRIES = [
  // South Asia
  'IN', 'PK', 'BD', 'LK', 'NP', 'BT', 'MV', 'AF',
  // East Asia
  'CN', 'JP', 'KR', 'KP', 'TW', 'HK', 'MO', 'MN',
  // Southeast Asia
  'SG', 'MY', 'ID', 'TH', 'VN', 'PH', 'KH', 'LA', 'MM', 'BN', 'TL',
  // Oceania
  'AU', 'NZ', 'PG', 'FJ', 'SB', 'VU', 'NC', 'PF', 'WS', 'TO', 'KI',
  'FM', 'MH', 'NR', 'PW', 'TV', 'CK', 'NU', 'TK', 'WF', 'AS', 'GU', 'MP', 'NF',
] as const;

/** North, Central and South America, including the Caribbean. */
export const AMERICAS_COUNTRIES = [
  // North America
  'US', 'CA', 'MX', 'GL', 'PM', 'BM',
  // Central America
  'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA',
  // Caribbean
  'CU', 'DO', 'HT', 'JM', 'PR', 'TT', 'BS', 'BB', 'AG', 'DM', 'GD', 'KN',
  'LC', 'VC', 'AW', 'CW', 'KY', 'VG', 'VI', 'TC', 'AI', 'MS', 'BQ', 'SX',
  'BL', 'MF', 'GP', 'MQ',
  // South America
  'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY', 'GY', 'SR', 'GF', 'FK',
] as const;

/** Europe, including the UK, the Balkans, Türkiye's neighbours and Russia. */
export const EUROPE_COUNTRIES = [
  'GB', 'IE', 'FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'LU', 'AT', 'CH',
  'LI', 'MC', 'AD', 'SM', 'VA', 'MT', 'GR', 'CY', 'DK', 'SE', 'NO', 'FI',
  'IS', 'EE', 'LV', 'LT', 'PL', 'CZ', 'SK', 'HU', 'SI', 'HR', 'BA', 'RS',
  'ME', 'MK', 'AL', 'BG', 'RO', 'MD', 'UA', 'BY', 'RU', 'GE', 'AM', 'AZ',
  'KZ', 'KG', 'TJ', 'TM', 'UZ', 'GI', 'FO', 'AX', 'JE', 'GG', 'IM', 'SJ', 'XK',
] as const;

/** Middle East and Africa. */
export const MEA_COUNTRIES = [
  // Middle East
  'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB', 'IL', 'PS', 'SY', 'IQ',
  'IR', 'YE', 'TR',
  // Africa
  'ZA', 'EG', 'NG', 'KE', 'MA', 'TN', 'DZ', 'LY', 'GH', 'ET', 'TZ', 'UG',
  'ZW', 'ZM', 'MZ', 'AO', 'BW', 'NA', 'SN', 'CI', 'CM', 'MU', 'RW', 'BI',
  'MW', 'MG', 'SD', 'SS', 'SO', 'DJ', 'ER', 'TD', 'NE', 'ML', 'BF', 'GN',
  'GW', 'SL', 'LR', 'TG', 'BJ', 'GA', 'CG', 'CD', 'CF', 'GQ', 'ST', 'CV',
  'GM', 'MR', 'LS', 'SZ', 'KM', 'SC', 'EH', 'YT', 'RE', 'SH',
] as const;

export interface CountryRegionPreset {
  key: 'APAC' | 'Americas' | 'Europe' | 'MEA';
  label: string;
  countries: readonly string[];
}

/**
 * APAC is deliberately first: it is the preset this feature was built for, and putting it first
 * makes the common case one click.
 */
export const COUNTRY_REGION_PRESETS: CountryRegionPreset[] = [
  { key: 'APAC', label: 'APAC', countries: APAC_COUNTRIES },
  { key: 'Americas', label: 'Americas', countries: AMERICAS_COUNTRIES },
  { key: 'Europe', label: 'Europe', countries: EUROPE_COUNTRIES },
  { key: 'MEA', label: 'MEA', countries: MEA_COUNTRIES },
];

/** Every selectable country code, de-duplicated and sorted by display name. */
export const ALL_COUNTRY_CODES: string[] = Array.from(new Set([
  ...APAC_COUNTRIES,
  ...AMERICAS_COUNTRIES,
  ...EUROPE_COUNTRIES,
  ...MEA_COUNTRIES,
]));

let displayNames: Intl.DisplayNames | null | undefined;

/**
 * Human-readable country name for a code, falling back to the code itself.
 *
 * `Intl.DisplayNames` is derived rather than stored so there is no second list to keep in sync,
 * but it is wrapped: it throws on some older runtimes, and a settings page must not blank out
 * because a label could not be resolved.
 */
export function countryName(code: string): string {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      displayNames = null;
    }
  }
  if (!displayNames) return code;
  try {
    return displayNames.of(code) || code;
  } catch {
    return code;
  }
}

/** Options for the country multi-select, sorted by the name the user actually reads. */
export function countryOptions(): { label: string; value: string }[] {
  return ALL_COUNTRY_CODES
    .map((code) => ({ label: `${countryName(code)} (${code})`, value: code }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Normalize a list the same way the backend does, so what the UI shows after a save matches what
 * was stored: uppercase, alpha-2 only, de-duplicated, first-seen order preserved.
 */
export function normalizeCountryCodes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  value.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const code = entry.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return;
    if (seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });
  return out;
}

/** Which presets are exactly satisfied by the current selection, for showing an active state. */
export function matchingPresetKeys(selected: readonly string[]): string[] {
  const set = new Set(selected);
  return COUNTRY_REGION_PRESETS
    .filter((preset) => preset.countries.length === set.size
      && preset.countries.every((code) => set.has(code)))
    .map((preset) => preset.key);
}
